import React, { useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import Home from './pages/Home';
import Collections from './pages/Collections';
import About from './pages/About';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import StoreLocator from './pages/StoreLocator';
import Services from './pages/Services';

function App() {
  const elementRef = useRef(null);

  const handleMouseMove = (e, element) => {
    if (!element) return;
    
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = ((y - centerY) / centerY) * 15;
    const rotateY = ((centerX - x) / centerX) * 15;
    
    element.style.transform = `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(30px)`;
  };

  const handleMouseLeave = (element) => {
    if (!element) return;
    element.style.transform = `perspective(1200px) rotateX(0deg) rotateY(0deg) translateZ(0px)`;
  };

  // Card-specific handlers (operate on inner .card-content so card background stays still)
  const handleCardMove = (e, element) => {
    if (!element) return;
    // element is the card-content element
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * 10; // subtler tilt for cards
    const rotateY = ((centerX - x) / centerX) * 10;
    element.style.transform = `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(20px)`;
  };

  const handleCardLeave = (element) => {
    if (!element) return;
    element.style.transform = `perspective(1200px) rotateX(0deg) rotateY(0deg) translateZ(0px)`;
  };

  // Modal / 3D viewer state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);

  const openModal = (product, triggerElement) => {
    // Brief zoom animation on the card, then open modal
    try {
      const card = triggerElement?.closest?.('.card-luxury-3d');
      if (card) {
        card.classList.add('card-zooming');
      }
    } catch (err) {
      // ignore
    }

    // Use product.model if available, otherwise fallback to sample ring
    const sampleRing = 'https://modelviewer.dev/shared-assets/models/Ring.glb';
    const modelToUse = product?.model || sampleRing;
    setTimeout(() => {
      setModalProduct({ ...product, model: modelToUse });
      setModalOpen(true);
      // remove zooming class after modal opens
      try {
        const card = triggerElement?.closest?.('.card-luxury-3d');
        if (card) {
          card.classList.remove('card-zooming');
        }
      } catch (err) {}
    }, 220);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalProduct(null);
  };

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-gray-950 overflow-x-hidden">
        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-950 shadow-2xl border-b border-yellow-600/20 backdrop-blur-sm">
          <div className="luxury-container flex justify-between items-center py-6">
            <Link 
              to="/"
              className="text-3xl font-bold tracking-widest brand-name brand-unique"
              ref={elementRef}
            >
              LUXE JEWELS
            </Link>
            <ul className="flex gap-10">
              <li><Link to="/" className="text-gray-300 hover:text-yellow-500 font-medium text-sm tracking-wide cursor-pointer">HOME</Link></li>
              <li><Link to="/collections" className="text-gray-300 hover:text-yellow-500 font-medium text-sm tracking-wide cursor-pointer">COLLECTIONS</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-yellow-500 font-medium text-sm tracking-wide cursor-pointer">ABOUT</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-yellow-500 font-medium text-sm tracking-wide cursor-pointer">BLOG</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-yellow-500 font-medium text-sm tracking-wide cursor-pointer">SERVICES</Link></li>
              <li><Link to="/stores" className="text-gray-300 hover:text-yellow-500 font-medium text-sm tracking-wide cursor-pointer">STORES</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-yellow-500 font-medium text-sm tracking-wide cursor-pointer">CONTACT</Link></li>
            </ul>
          </div>
        </nav>

        {/* Routes */}
        <Routes>
          <Route path="/" element={<Home handleMouseMove={handleMouseMove} handleMouseLeave={handleMouseLeave} />} />
          <Route path="/collections" element={<Collections handleMouseMove={handleMouseMove} handleMouseLeave={handleMouseLeave} handleCardMove={handleCardMove} handleCardLeave={handleCardLeave} openModal={openModal} />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/services" element={<Services />} />
          <Route path="/stores" element={<StoreLocator />} />
          <Route path="/contact" element={<Contact handleMouseMove={handleMouseMove} handleMouseLeave={handleMouseLeave} />} />
        </Routes>

        {/* Footer */}
        <footer className="bg-gray-950/80 border-t border-yellow-600/20 mt-auto">
          <div className="luxury-container py-12">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-semibold text-yellow-400 mb-4">LUXE JEWELS</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Crafting timeless elegance since 2026. Discover the art of luxury jewelry that transcends generations.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-yellow-400 mb-4">Quick Links</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/collections" className="text-gray-400 hover:text-yellow-500 transition-colors">Collections</Link></li>
                  <li><Link to="/about" className="text-gray-400 hover:text-yellow-500 transition-colors">About Us</Link></li>
                  <li><Link to="/blog" className="text-gray-400 hover:text-yellow-500 transition-colors">Jewelry Insights</Link></li>
                  <li><Link to="/services" className="text-gray-400 hover:text-yellow-500 transition-colors">Services</Link></li>
                  <li><Link to="/stores" className="text-gray-400 hover:text-yellow-500 transition-colors">Store Locator</Link></li>
                  <li><Link to="/contact" className="text-gray-400 hover:text-yellow-500 transition-colors">Contact</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-yellow-400 mb-4">Resources</h3>
                <ul className="space-y-2 text-sm">
                  <li><button className="text-gray-400 hover:text-yellow-500 transition-colors">Digital Catalog</button></li>
                  <li><button className="text-gray-400 hover:text-yellow-500 transition-colors">Care Guide</button></li>
                  <li><button className="text-gray-400 hover:text-yellow-500 transition-colors">Size Guide</button></li>
                  <li><button className="text-gray-400 hover:text-yellow-500 transition-colors">Gift Registry</button></li>
                  <li><button className="text-gray-400 hover:text-yellow-500 transition-colors">Schedule Appointment</button></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-yellow-400 mb-4">Newsletter</h3>
                <p className="text-gray-400 text-sm mb-4">Stay updated with the latest in luxury jewelry and exclusive offers.</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Your email"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-yellow-600/30 rounded text-white text-sm focus:outline-none focus:border-yellow-500"
                  />
                  <button className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-black font-medium text-sm rounded transition-colors">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
            <div className="border-t border-yellow-600/20 pt-8 text-center">
              <p className="text-yellow-400/60 tracking-widest text-sm">
                &copy; 2026 LUXE JEWELS &bull; ELEGANCE IS TIMELESS &bull; QUALITY IS ETERNAL
              </p>
            </div>
          </div>
        </footer>

        {/* Modal 3D Viewer */}
        {modalOpen && modalProduct && (
          <div className="model-backdrop" onClick={closeModal}>
            <div className="model-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>&times;</button>
              <div className="flex flex-col lg:flex-row gap-6 h-full">
                <div className="flex-1">
                  <h3 className="modal-title mb-4">{modalProduct.name}</h3>
                  {modalProduct.model ? (
                    <div style={{ position: 'relative', height: '400px' }}>
                      <model-viewer src={modalProduct.model} alt={modalProduct.name} camera-controls auto-rotate interaction-prompt="auto" shadow-intensity="1" exposure="1" style={{ width: '100%', height: '100%' }}></model-viewer>
                      <div className="viewer-sparkle" aria-hidden="true"></div>
                    </div>
                  ) : (
                    <div className="modal-fallback h-64 flex items-center justify-center">3D preview not available</div>
                  )}
                </div>
                <div className="lg:w-80 bg-gray-900/50 p-6 rounded-lg">
                  <h4 className="text-xl font-semibold text-yellow-400 mb-4">Product Details</h4>
                  <div className="space-y-4">
                    <div>
                      <span className="text-gray-400 text-sm">Price:</span>
                      <p className="text-2xl font-bold text-yellow-500">{modalProduct.price}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Description:</span>
                      <p className="text-gray-300 mt-1">{modalProduct.description}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Materials:</span>
                      <p className="text-gray-300 mt-1">18K Gold, Premium Gemstones, Diamond Accents</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Care:</span>
                      <p className="text-gray-300 mt-1 text-sm">Store in a cool, dry place. Clean with soft cloth. Professional cleaning recommended annually.</p>
                    </div>
                    <button className="w-full bg-yellow-600 hover:bg-yellow-500 text-black font-semibold py-3 rounded transition-colors mt-6">
                      INQUIRE NOW
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Router>
  );
}

export default App;
